import React, { useState, useEffect, useContext } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../components/Layout';
import { 
    Box, Typography, Alert, Snackbar, Chip, 
    useTheme, useMediaQuery, Card, CardContent, Stack,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, Tooltip, FormControlLabel, Switch,
    Tabs, Tab, Divider, Paper
} from '@mui/material';
import axios from 'axios';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PeopleIcon from '@mui/icons-material/People';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AuthContext } from '../../AuthContext';
import LogoUpload from '../../components/LogoUpload';

const ReferredPartnersGrid = () => {
    const [rows, setRows] = useState([]);
    const [indirectRows, setIndirectRows] = useState([]);
    const [gridHeight, setGridHeight] = useState(400);
    const [gridLoading, setGridLoading] = useState(false);
    const [indirectGridLoading, setIndirectGridLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerCode, setPartnerCode] = useState('');
    const [partnerLogo, setPartnerLogo] = useState('');
    const [authorised, setAuthorised] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const { user } = useContext(AuthContext);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        fetchData();
        fetchIndirectData();
    }, []);
    
    useEffect(() => {
        const viewportHeight = window.innerHeight;
        const calculatedHeight = viewportHeight - 150;
        setGridHeight(calculatedHeight);
    }, []); 
    
    const fetchData = () => {
        setGridLoading(true);
        axios.get('/api/partners/referred-partners')
        .then(result => {
            setGridLoading(false);
            setRows(result.data);
        })
        .catch(error => {
            setGridLoading(false);
            console.error('Error fetching referred partners:', error);
            setErrorMessage('Error loading referred partners. Please try again.');
            setShowAlert(true);
        });
    };

    const fetchIndirectData = () => {
        setIndirectGridLoading(true);
        axios.get('/api/partners/indirect-referred-partners')
        .then(result => {
            setIndirectGridLoading(false);
            setIndirectRows(result.data);
        })
        .catch(error => {
            setIndirectGridLoading(false);
            console.error('Error fetching indirect referred partners:', error);
            // Don't show error for indirect partners as it's supplementary data
        });
    };

    const handleVerifyPartner = (partner) => {
        setSelectedPartner(partner);
        setPartnerCode(partner.PartnerCode || ''); // Read-only display
        setPartnerLogo(partner.PartnerLogo || '');
        setAuthorised(partner.Authorised);
        setVerifyDialogOpen(true);
    };

    const handleVerifySubmit = async () => {
        if (!selectedPartner) return;

        setVerifying(true);
        try {
            await axios.post(`/api/partners/referred-partners/${selectedPartner.id}/verify`, {
                partnerLogo: partnerLogo.trim() || null,
                authorised: authorised
                // partnerCode is auto-generated and not editable
            });

            setSuccessMessage(authorised ? 'Partner verified successfully!' : 'Partner unverified successfully!');
            setShowSuccess(true);
            setVerifyDialogOpen(false);
            fetchData(); // Refresh the data
            fetchIndirectData(); // Refresh indirect data as it may have changed
        } catch (error) {
            console.error('Error verifying partner:', error);
            setErrorMessage(error.response?.data?.error || 'Error updating partner. Please try again.');
            setShowAlert(true);
        } finally {
            setVerifying(false);
        }
    };

    const handleCloseVerifyDialog = () => {
        setVerifyDialogOpen(false);
        setSelectedPartner(null);
        setPartnerCode('');
        setPartnerLogo('');
        setAuthorised(true);
    };

    const columns = [
        { 
            field: "name", 
            headerName: "Company Name", 
            flex: 1,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="primary" />
                    <Typography>{params.value}</Typography>
                </Box>
            )
        },
        { field: "email", headerName: "Email", flex: 1 },
        { 
            field: "CompanyNumber", 
            headerName: "Company Number", 
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value || 'N/A'}
                </Typography>
            )
        },
        { 
            field: "managementUser", 
            headerName: "Contact Person", 
            flex: 1,
            renderCell: (params) => {
                const user = params.value;
                return user ? `${user.first_name} ${user.last_name}` : 'N/A';
            }
        },
        { 
            field: "Authorised", 
            headerName: "Status", 
            width: 150,
            renderCell: (params) => (
                <Chip 
                    label={params.value ? "Authorised" : "Pending"} 
                    color={params.value ? "success" : "warning"}
                    variant="outlined"
                    size="small"
                />
            )
        },
        { 
            field: "createdAt", 
            headerName: "Referred Date", 
            width: 150,
            renderCell: (params) => {
                return new Date(params.value).toLocaleDateString();
            }
        },
        { 
            field: "PartnerCode", 
            headerName: "Partner Code", 
            width: 150,
            renderCell: (params) => (
                <Chip 
                    label={params.value || 'Not Set'} 
                    color={params.value ? "primary" : "default"}
                    variant="outlined"
                    size="small"
                />
            )
        },
        { 
            field: "customerCount", 
            headerName: "Customers", 
            width: 120,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <PeopleIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight="medium">
                        {params.value || 0}
                    </Typography>
                </Box>
            )
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 150,
            sortable: false,
            renderCell: (params) => {
                const partner = params.row;
                return (
                    <Box display="flex" gap={1}>
                        {!partner.Authorised ? (
                            <Tooltip title="Verify Partner">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleVerifyPartner(partner)}
                                >
                                    <CheckCircleIcon />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Edit Partner Settings">
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleVerifyPartner(partner)}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        }
    ];

    const indirectColumns = [
        { 
            field: "name", 
            headerName: "Company Name", 
            flex: 1,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <AccountTreeIcon color="secondary" />
                    <Typography>{params.value}</Typography>
                </Box>
            )
        },
        { field: "email", headerName: "Email", flex: 1 },
        { 
            field: "CompanyNumber", 
            headerName: "Company Number", 
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value || 'N/A'}
                </Typography>
            )
        },
        { 
            field: "managementUser", 
            headerName: "Contact Person", 
            flex: 1,
            renderCell: (params) => {
                const user = params.value;
                return user ? `${user.first_name} ${user.last_name}` : 'N/A';
            }
        },
        { 
            field: "referralLevel", 
            headerName: "Level", 
            width: 100,
            renderCell: (params) => (
                <Chip 
                    label={`L${params.value}`} 
                    color="info"
                    variant="outlined"
                    size="small"
                />
            )
        },
        { 
            field: "Authorised", 
            headerName: "Status", 
            width: 150,
            renderCell: (params) => (
                <Chip 
                    label={params.value ? "Authorised" : "Pending"} 
                    color={params.value ? "success" : "warning"}
                    variant="outlined"
                    size="small"
                />
            )
        },
        { 
            field: "createdAt", 
            headerName: "Referred Date", 
            width: 150,
            renderCell: (params) => {
                return new Date(params.value).toLocaleDateString();
            }
        },
        { 
            field: "PartnerCode", 
            headerName: "Partner Code", 
            width: 150,
            renderCell: (params) => (
                <Chip 
                    label={params.value || 'Not Set'} 
                    color={params.value ? "primary" : "default"}
                    variant="outlined"
                    size="small"
                />
            )
        },
        { 
            field: "customerCount", 
            headerName: "Customers", 
            width: 120,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <PeopleIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight="medium">
                        {params.value || 0}
                    </Typography>
                </Box>
            )
        }
    ];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Layout title='Referred Partners'> 
            <Box my={4}>
                <Box sx={{ 
                    mb: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        Manage your partner referral network
                    </Typography>
                </Box>

                <Paper sx={{ mb: 3 }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        indicatorColor="primary"
                        textColor="primary"
                        variant={isMobile ? "fullWidth" : "standard"}
                    >
                        <Tab 
                            icon={<BusinessIcon />} 
                            label={`Direct Referrals (${rows.length})`} 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<AccountTreeIcon />} 
                            label={`Indirect Referrals (${indirectRows.length})`} 
                            iconPosition="start"
                        />
                    </Tabs>
                </Paper>

                {tabValue === 0 && (
                    isMobile ? (
                        <>
                            <Stack spacing={2}>
                                {rows.map((row) => (
                                <Card key={row.id}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <BusinessIcon color="primary" />
                                            <Typography variant="h6">
                                                {row.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" gutterBottom>
                                            {row.email}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Company Number: {row.CompanyNumber || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Contact: {row.managementUser ? `${row.managementUser.first_name} ${row.managementUser.last_name}` : 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Referred: {new Date(row.createdAt).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <PeopleIcon color="action" sx={{ fontSize: 16 }} />
                                                <span>Customers: {row.customerCount || 0}</span>
                                            </Box>
                                        </Typography>
                                        
                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                            <Chip 
                                                label={row.Authorised ? "Authorised" : "Pending"} 
                                                color={row.Authorised ? "success" : "warning"}
                                                variant="outlined"
                                                size="small"
                                            />
                                            {row.PartnerCode && (
                                                <Chip 
                                                    label={`Code: ${row.PartnerCode}`} 
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Stack>
                                        
                                        <Box sx={{ mt: 2 }}>
                                            {!row.Authorised ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<CheckCircleIcon />}
                                                    onClick={() => handleVerifyPartner(row)}
                                                    fullWidth
                                                >
                                                    Verify Partner
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleVerifyPartner(row)}
                                                    fullWidth
                                                >
                                                    Edit Settings
                                                </Button>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                                ))}
                            </Stack>
                        </>
                    ) : (
                        <Box sx={{ height: gridHeight }}>
                            <DataGrid 
                                rows={rows} 
                                columns={columns} 
                                loading={gridLoading}
                                pageSize={10} 
                                components={{
                                    Header: {
                                        cell: (params) => <strong>{params.value}</strong>
                                    }
                                }}
                            />
                        </Box>
                    )
                )}

                {tabValue === 1 && (
                    isMobile ? (
                        <>
                            <Stack spacing={2}>
                                {indirectRows.map((row) => (
                                <Card key={row.id}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <AccountTreeIcon color="secondary" />
                                            <Typography variant="h6">
                                                {row.name}
                                            </Typography>
                                            <Chip 
                                                label={`L${row.referralLevel}`} 
                                                color="info"
                                                variant="outlined"
                                                size="small"
                                                sx={{ ml: 'auto' }}
                                            />
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" gutterBottom>
                                            {row.email}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Company Number: {row.CompanyNumber || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Contact: {row.managementUser ? `${row.managementUser.first_name} ${row.managementUser.last_name}` : 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Referred: {new Date(row.createdAt).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <PeopleIcon color="action" sx={{ fontSize: 16 }} />
                                                <span>Customers: {row.customerCount || 0}</span>
                                            </Box>
                                        </Typography>
                                        
                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                            <Chip 
                                                label={row.Authorised ? "Authorised" : "Pending"} 
                                                color={row.Authorised ? "success" : "warning"}
                                                variant="outlined"
                                                size="small"
                                            />
                                            {row.PartnerCode && (
                                                <Chip 
                                                    label={`Code: ${row.PartnerCode}`} 
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                                ))}
                            </Stack>
                        </>
                    ) : (
                        <Box sx={{ height: gridHeight }}>
                            <DataGrid 
                                rows={indirectRows} 
                                columns={indirectColumns} 
                                loading={indirectGridLoading}
                                pageSize={10} 
                                components={{
                                    Header: {
                                        cell: (params) => <strong>{params.value}</strong>
                                    }
                                }}
                            />
                        </Box>
                    )
                )}

                {tabValue === 0 && rows.length === 0 && !gridLoading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Direct Referrals Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            When partners sign up using your referral link, they will appear here.
                        </Typography>
                    </Box>
                )}

                {tabValue === 1 && indirectRows.length === 0 && !indirectGridLoading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <AccountTreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Indirect Referrals Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            When your direct referrals start referring partners, they will appear here.
                        </Typography>
                    </Box>
                )}
            </Box>

            {showAlert && (
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    open={showAlert}
                    autoHideDuration={10000}
                    message={errorMessage}
                    onClose={() => setShowAlert(false)}
                >
                    <Alert severity="error" onClose={() => setShowAlert(false)}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
            )}

            {showSuccess && (
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    open={showSuccess}
                    autoHideDuration={5000}
                    message={successMessage}
                    onClose={() => setShowSuccess(false)}
                >
                    <Alert severity="success" onClose={() => setShowSuccess(false)}>
                        {successMessage}
                    </Alert>
                </Snackbar>
            )}

            {/* Verify Partner Dialog */}
            <Dialog 
                open={verifyDialogOpen} 
                onClose={handleCloseVerifyDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {selectedPartner?.Authorised ? 'Edit Partner Settings' : 'Verify Partner'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedPartner?.Authorised 
                                ? 'Update the partner settings or unverify this partner. The partner code was auto-generated and cannot be changed.'
                                : 'Verify this partner to enable them to refer other partners. Their unique partner code has been automatically generated.'
                            }
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={authorised}
                                    onChange={(e) => setAuthorised(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Partner Authorised"
                            sx={{ mb: 2 }}
                        />
                        
                        {partnerCode && (
                            <>
                                <TextField
                                    fullWidth
                                    label="Partner Code"
                                    value={partnerCode}
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    helperText="Auto-generated unique code for this partner"
                                    sx={{ backgroundColor: 'grey.50' }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Partner Signup URL"
                                    value={`${window.location.origin}/partner-signup?partner=${partnerCode}`}
                                    margin="normal"
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`${window.location.origin}/partner-signup?partner=${partnerCode}`);
                                                    setSuccessMessage('URL copied to clipboard!');
                                                    setShowSuccess(true);
                                                }}
                                                edge="end"
                                                title="Copy URL"
                                            >
                                                <ContentCopyIcon />
                                            </IconButton>
                                        )
                                    }}
                                    helperText="Share this URL for partner referrals"
                                    sx={{ backgroundColor: 'grey.50' }}
                                />
                            </>
                        )}
                        
                        {authorised && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Partner Logo:
                                </Typography>
                                <LogoUpload
                                    currentLogo={partnerLogo}
                                    onLogoChange={(newLogoUrl) => setPartnerLogo(newLogoUrl || '')}
                                    onError={(error) => {
                                        setErrorMessage(error);
                                        setShowAlert(true);
                                    }}
                                    onSuccess={(message) => {
                                        setSuccessMessage(message);
                                        setShowSuccess(true);
                                    }}
                                    partnerId={selectedPartner?.id}
                                    compact={true}
                                    showCurrentLogo={true}
                                    allowDelete={true}
                                    uploadEndpoint="/api/partners/logo"
                                    deleteEndpoint="/api/partners/logo"
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseVerifyDialog} disabled={verifying}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleVerifySubmit} 
                        variant="contained" 
                        disabled={verifying}
                        startIcon={verifying ? <CircularProgress size={16} /> : null}
                    >
                        {verifying ? 'Updating...' : (selectedPartner?.Authorised ? 'Update' : 'Verify')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
}

export default ReferredPartnersGrid; 